import { computed, onMounted, ref, watch } from 'vue'
import { usePermission } from './use-permission'
import type { Member } from '../models/member/member'
import type { Team } from '../models/team/team'
import { memberShortName } from '../models/member/member-name'
import type { RouteParams } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Permission } from '../models/user'
import { useMembersStore } from '../../stores/members.store'
import { useTeamsStore } from '../../stores/teams.store'
import { useApplicationModule } from './use-application-module'
import { ApplicationModule } from '../models/organization'
import { useRoute, useRouter } from 'vue-router'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { storeToRefs } from 'pinia'

export const useNavigation = () => {
  const { can, canAny } = usePermission()
  const { hasModule } = useApplicationModule()
  const { t } = useI18n()
  const memberStore = useMembersStore()
  const teamStore = useTeamsStore()
  const router = useRouter()

  const feedbackTopicStore = useFeedbackTopicStore()
  const { journalTemplates, evaluationTemplates } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  onMounted(() => {
    loadTemplates()
  })

  const hasJournalTemplates = computed(() => {
    return journalTemplates.value.length > 0
  })

  const hasEvaluationTemplates = computed(() => {
    return evaluationTemplates.value.length > 0
  })

  const navigationItems = computed(() => {
    const groups: NavigationGroup[] = []
    mainGroup(groups)
    dialogGroup(groups)
    feedbackGroup(groups)
    coachGroup(groups)
    lumiaGroup(groups)
    teamsGroup(groups)

    return groups
  })

  const availableContexts = computed(() => {
    const contexts: AppContext[] = []
    const availableGroupIds = new Set(navigationItems.value.map((group) => group.id))

    const personalGroupIds = ['common', 'dialog', 'feedback'].filter((groupId) =>
      availableGroupIds.has(groupId),
    )

    if (personalGroupIds.length > 0) {
      contexts.push({
        icon: 'person',
        id: 'personal',
        title: t('appContext.personal'),
        navigationGroupIds: personalGroupIds,
        fabActionIds: ['give-feedback', 'request-feedback', 'start-self-feedback'],
        fabLabel: t('common.feedback'),
        defaultRouteName: hasModule(ApplicationModule.LUMIA) ? 'profile' : 'dashboard',
      })
    }

    if (availableGroupIds.has('coach')) {
      const fabActionIds = ['start-survey-for-coachees']
      if (hasEvaluationTemplates.value) {
        if (hasModule(ApplicationModule.ASSESSMENT)) {
          fabActionIds.push('start-assessment')
        } else {
          fabActionIds.push('create-feedback-goal')
        }
      }
      contexts.push({
        icon: 'supervisor_account',
        id: 'team',
        title: t('appContext.team'),
        navigationGroupIds: ['coach', 'coachees'],
        fabActionIds: fabActionIds,
        fabLabel: t('common.new'),
        defaultRouteName: 'coachees',
      })
    }

    if (availableGroupIds.has('lumia')) {
      contexts.push({
        icon: 'library_books',
        id: 'lumia',
        title: t('appContext.lumia'),
        navigationGroupIds: ['lumia', 'teams'],
        fabActionIds: [],
        fabLabel: '',
        defaultRouteName: 'impulse-library',
      })
    }

    return contexts
  })

  const getContextFromCurrentRoute = () => {
    const route = useRoute()
    return (
      availableContexts.value.find((context) => {
        const navigationGroupIds = context.navigationGroupIds
        return navigationGroupIds.some((groupId) => {
          const group = navigationItems.value.find((group) => group.id === groupId)
          if (group) {
            const routeName = String(route.name ?? '')
            return group.items.some(
              (item) => item.routeName === routeName || routeName.startsWith(`${item.routeName}-`),
            )
          }
          return false
        })
      }) || availableContexts.value[0]
    )
  }

  const selectedContextId = ref(getContextFromCurrentRoute().id)

  const selectedContext = computed(
    () =>
      availableContexts.value.find((context) => context.id === selectedContextId.value) ||
      availableContexts.value[0],
  )

  watch(availableContexts, (contexts) => {
    if (!contexts.some((context) => context.id === selectedContextId.value)) {
      selectedContextId.value = contexts[0]?.id
    }
  })

  watch(selectedContext, (newContext, oldContext) => {
    if (newContext.id !== oldContext?.id) {
      router.push({ name: newContext.defaultRouteName })
    }
  })

  function mainGroup(groups: NavigationGroup[]) {
    const items: NavigationItem[] = []
    if (!hasModule(ApplicationModule.LUMIA)) {
      items.push({
        iconName: 'home',
        routeName: 'dashboard',
        label: t('mainNavigation.dashboard'),
      })
    }

    if (can(Permission.FEEDBACK_RECEIVE)) {
      items.push({
        iconName: 'widgets',
        routeName: 'profile',
        label: t('mainNavigation.profile'),
      })
    }

    if (items.length > 0) {
      groups.push({
        id: 'common',
        items: items,
      })
    }
  }

  function dialogGroup(groups: NavigationGroup[]) {
    const items: NavigationItem[] = []
    if (hasModule(ApplicationModule.DIALOG) && can(Permission.FEEDBACK_RECEIVE)) {
      items.push({
        iconName: 'forum',
        routeName: 'dialogs',
        label: t('mainNavigation.currentDialog'),
      })
      items.push({
        iconName: 'history',
        routeName: 'conducted-dialogs',
        label: t('mainNavigation.conductedDialogs'),
      })
      items.push({
        iconName: 'target',
        routeName: 'objectives',
        label: t('mainNavigation.objectives'),
      })
    }
    if (items.length > 0) {
      groups.push({
        id: 'dialog',
        title: t('mainNavigation.dialog'),
        items: items,
      })
    }
  }

  function feedbackGroup(groups: NavigationGroup[]) {
    const items: NavigationItem[] = []
    if (hasModule(ApplicationModule.LUMIA)) {
      return items
    }

    if (canAny([Permission.FEEDBACK_GIVE, Permission.FEEDBACK_RECEIVE])) {
      items.push({
        iconName: 'list_alt_check',
        routeName: 'todos',
        label: t('mainNavigation.todos'),
      })
    }

    if (can(Permission.FEEDBACK_INITIATE)) {
      items.push({
        iconName: 'ballot',
        routeName: 'surveys',
        label: t('mainNavigation.surveys'),
      })
    }

    if (canAny([Permission.FEEDBACK_READ_GIVEN, Permission.FEEDBACK_RECEIVE])) {
      items.push({
        iconName: 'history',
        routeName: 'feedbacks',
        label: t('mainNavigation.feedbackHistory'),
      })
    }

    if (items.length > 0) {
      groups.push({
        id: 'feedback',
        title: t('mainNavigation.feedbacks'),
        items: items,
      })
    }
  }

  function coachGroup(groups: NavigationGroup[]) {
    const items: NavigationItem[] = []
    const coachees: NavigationItem[] = []

    if (hasModule(ApplicationModule.COACHING) && can(Permission.COACHING_ASSIGN)) {
      items.push({
        iconName: 'group',
        routeName: 'coachees',
        label: t('mainNavigation.coach-overview'),
      })

      if (hasJournalTemplates.value) {
        items.push({
          iconName: 'vital_signs',
          routeName: 'coach-surveys',
          label: t('mainNavigation.coachSurveys'),
        })
      }
      if (hasEvaluationTemplates.value) {
        if (hasModule(ApplicationModule.ASSESSMENT)) {
          items.push({
            iconName: 'assignment',
            routeName: 'assessments',
            label: t('mainNavigation.assessments'),
          })
        } else {
          items.push({
            iconName: 'flag',
            routeName: 'coach-feedback-goals',
            label: t('mainNavigation.coachFeedbackGoals'),
          })
        }
      }

      for (const member of memberStore.allCoachees) {
        coachees.push({
          member: member,
          routeName: 'coachee',
          params: { id: member.id },
          label: memberShortName(member),
        })
      }
    }

    if (items.length > 0) {
      groups.push({
        id: 'coach',
        items: items,
      })
    }

    if (coachees.length > 0) {
      groups.push({
        title: t('mainNavigation.coachees'),
        id: 'coachees',
        items: coachees,
      })
    }
  }

  function teamsGroup(groups: NavigationGroup[]) {
    if (!can(Permission.TEAM_VIEW_MEMBERS)) {
      return
    }

    const items: NavigationItem[] = teamStore.allTeams.map((team) => ({
      team,
      routeName: 'team',
      params: { teamId: team.id },
      label: team.name,
    }))

    if (items.length > 0) {
      groups.push({
        id: 'teams',
        title: t('mainNavigation.teams'),
        items,
      })
    }
  }

  function lumiaGroup(groups: NavigationGroup[]) {
    const items: NavigationItem[] = []
    if (hasModule(ApplicationModule.LUMIA)) {
      items.push({
        iconName: 'library_books',
        routeName: 'impulse-library',
        label: t('lumia.lesson.pageTitle'),
      })
    }
    if (items.length > 0) {
      groups.push({
        id: 'lumia',
        items: items,
      })
    }
  }

  return {
    navigationItems,
    availableContexts,
    selectedContextId,
    selectedContext,
    hasJournalTemplates,
    hasEvaluationTemplates,
  }
}

export interface NavigationGroup {
  id: string
  title?: string
  items: NavigationItem[]
}

export interface NavigationItem {
  iconName?: string
  member?: Member
  team?: Team
  routeName: string
  params?: RouteParams
  label: string
  badgeCount?: number
}

type AppContextId = 'personal' | 'team' | 'organization' | 'lumia'

interface AppContext {
  icon: string
  id: AppContextId
  title: string
  navigationGroupIds: string[]
  fabActionIds: string[]
  fabLabel: string
  defaultRouteName: string
}
